// This is the update to RegisterForm.tsx to handle the userType field properly
// The key parts that need updating are shown below

import css from './RegisterForm.module.css';

import TextInput from '../../Common/TextInput/TextInput';
import Validators from '../../../utility/validators';
import {
  ErrorCode,
  LoginPageAction,
  FormField,
  FormStatus,
  StatusUtil,
} from '../../../types/appTypes';
import { useForm } from '../../../hooks/useForm';
import DataForm from '../../Common/DataForm/DataForm';
import CheckboxInput from '../../Common/CheckboxInput/CheckboxInput';

type RegisterFormProps = {
  onCancel: (action: LoginPageAction) => void;
};

type RegisterFormFields = {
  email: FormField<string>;
  name: FormField<string>;
  apiRequest: FormField<boolean>;
};

const RegisterForm = ({ onCancel }: RegisterFormProps) => {
  const { fields, formState, setFields, setFormState } =
    useForm<RegisterFormFields>({
      initialFields: {
        email: { value: '', valid: false },
        name: { value: '', valid: false },
        apiRequest: { value: false, valid: true },
      },
    });

  const formIsValid =
    fields.email.valid && fields.name.valid && fields.apiRequest.valid;

  const formRequiredInputChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    const valid = Validators.required(value);
    setFields((pv) => {
      return { ...pv, [name]: { value: value, valid: valid } };
    });
  };

  const checkboxInputChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = event.target;
    setFields((pv) => {
      return { ...pv, [name]: { value: checked, valid: true } };
    });
  };

  const emailValidationHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    let valid = Validators.required(value);
    let message: string | undefined = undefined;
    if (valid) {
      if (value.toLowerCase().endsWith('@libertyenergy.com')) {
        valid = false;
        message = 'As a Liberty employee, please reach out to <a href="https://outlook.office.com/mail/deeplink/compose?to=atlas.support@libertyenergy.com" style="color: inherit">Atlas support</a>!';
      }
    }

    setFields((pv) => {
      return {
        ...pv,
        [name]: { value: value, valid: valid, message: message },
      };
    });
  };

  const cancelHandler = (event: React.MouseEvent) => {
    onCancel(LoginPageAction.Login);
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    if (!formIsValid) {
      return;
    }

    setFormState({ status: FormStatus.Submitting });

    fetch(
      `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_REGISTRATION}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          regEmail: fields.email.value.toLowerCase(),
          name: fields.name.value,
          apiRequest: fields.apiRequest.value,
          atlasRequest: true,
          userType: 'User', // Default to "User" for self-registrations
        }),
      }
    )
      .then((response) => {
        response.json().then((data) => {
          if (response.status === 200) {
            setFormState({ status: FormStatus.Success });
          } else {
            setFormState({ status: FormStatus.Fail, error: data });
          }
        });
      })
      .catch((err) => {
        setFormState({ status: FormStatus.Error });
      });
  };

  let errorMessage: string | undefined = undefined;
  if (StatusUtil.isFail(formState.status)) {
    errorMessage = 'An error occurred and your registration was not received.';
    if (formState.status === FormStatus.Fail && formState.error != null) {
      if (formState.error.code === ErrorCode.userAlreadyExists)
        errorMessage = 'A user with this email already exists';

      if (formState.error.code === ErrorCode.regAlreadyExists)
        errorMessage = 'A registration with this email already exists';
    }
  }

  return (
    <DataForm
      title='CREATE ACCOUNT'
      successMessage='Your registration has been received and will be reviewed.'
      submitErrorMessage={errorMessage}
      secondaryContainerClass={css.container}
      status={formState.status}
      onSubmit={formSubmitHandler}
    >
      <div className={css.formGroup}>
        <TextInput
          placeholder='Email'
          type='email'
          name='email'
          value={fields.email.value}
          valid={fields.email.valid}
          statusText={fields.email.message}
          onChange={emailValidationHandler}
        />
        <TextInput
          placeholder='Name'
          type='text'
          name='name'
          value={fields.name.value}
          valid={fields.name.valid}
          onChange={formRequiredInputChangeHandler}
        />
        <CheckboxInput
          label='Request API Access'
          name='apiRequest'
          value={fields.apiRequest.value}
          valid={fields.apiRequest.valid}
          onChange={checkboxInputChangeHandler}
        />
      </div>
      <div className={css.actions}>
        <button type='submit' disabled={!formIsValid}>
          Submit
        </button>
        <button onClick={cancelHandler}>Cancel</button>
      </div>
    </DataForm>
  );
};

export default RegisterForm;